import { Base, Geometry, Subtraction } from "@react-three/csg";
import { Texture, TextureLoader, Vector3 } from "three";
import { useLoader } from "@react-three/fiber";
import React, { useEffect, useState } from "react";
import { useConfigurationOptionsContext } from "../../../app/providers/DoorOptionsProvider";
import { OptionTemplate } from "../../../shared/api/middleware-api-client";
import { getFullAssetPath } from "../../../shared/utils/assets";
import {
  defaultDoorHeight,
  defaultDoorWidth,
} from "../../../shared/utils/doorSceneConstants";
import { newTextureXLXLDoors } from "features/option-controls/door-width-controls/XLXXLHelper";
import { getMarginMapping } from "./ForegroundHelper";

interface ForegroundConfiguration {
  texture: Texture;
  subtractionPositionConfig: SubtractionPositionConfig;
  biggerSceneNeeded?: boolean;
  leftMargin?: number;
  rightMargin?: number;
  topMargin?: number;
  bottomMargin?: number;
}

interface SubtractionPositionConfig {
  xPosition: number;
  minXPosition: number;
  maxXPosition: number;
  yPosition: number;
  minYPosition: number;
  maxYPosition: number;
}

interface ForegroundProps {
  door: OptionTemplate | null;
  doorPositionXOffset: number;
  doorScale: Vector3;
}

const Foreground: React.FC<ForegroundProps> = ({
  door,
  doorPositionXOffset,
  doorScale,
}) => {
  const {
    layout,
    eigenwijs,
    minDoorWidth,
    maxDoorWidth,
    isXLXXLScene,
    getConfigurationWidth,
  } = useConfigurationOptionsContext();

  const [subtractionScale, setSubtractionScale] = useState<Vector3>(
    new Vector3(0, 0, 0),
  );
  const [subtractionPosition, setSubtractionPosition] = useState<Vector3>(
    new Vector3(0, 0, 0),
  );

  const interiorClosed1Unit2UnitTexture = useLoader(
    TextureLoader,
    getFullAssetPath("/img-v2/interior-closed-1-unit-2-unit.jpg"),
  );
  const interiorClosed3Unit4UnitTexture = useLoader(
    TextureLoader,
    getFullAssetPath("/img-v2/interior-closed-3-unit-4-unit.jpg"),
  );
  const interiorClosedStairsTexture = useLoader(
    TextureLoader,
    getFullAssetPath("/img-v2/interior-open-stairs.jpg"),
  );
  const interiorStudioTexture = useLoader(
    TextureLoader,
    getFullAssetPath("/img-v2/interior-studio.jpg"),
  );

  const interiorXLXXLTexture = useLoader(
    TextureLoader,
    getFullAssetPath("/img-v2/interior-xlxxl-closed.jpg"),
  );

  const defaultOneTwoElementSubPosConfig: SubtractionPositionConfig = {
    xPosition: 0,
    minXPosition: 0.15,
    maxXPosition: 0.084,
    yPosition: 0.092,
    minYPosition: -0.33,
    maxYPosition: 0.22,
  };

  const defaultThreeElementSubPosConfig: SubtractionPositionConfig = {
    xPosition: 0.01,
    minXPosition: 0.15,
    maxXPosition: 0.084,
    yPosition: 0.307,
    minYPosition: -0.22,
    maxYPosition: 0.17,
  };

  const defaultStairSubPosConfig: SubtractionPositionConfig = {
    xPosition: 0.11,
    minXPosition: 0.15,
    maxXPosition: 0.084,
    yPosition: 0,
    minYPosition: -0.3,
    maxYPosition: 0.12,
  };

  const defaultFourElementSubPosConfig: SubtractionPositionConfig = {
    xPosition: -0.07,
    minXPosition: 0.15,
    maxXPosition: 0.08,
    yPosition: 0.36,
    minYPosition: -0.17,
    maxYPosition: 0.12,
  };

  const paneel4x: SubtractionPositionConfig = {
    xPosition: -0.09,
    minXPosition: 0.15,
    maxXPosition: 0.08,
    yPosition: 0.334,
    minYPosition: -0.27,
    maxYPosition: 0.106,
  };

  const foregroundRecord: Record<string, ForegroundConfiguration> = {
    opdekdeurmetkozijn: {
      texture: interiorClosed1Unit2UnitTexture,
      subtractionPositionConfig: defaultOneTwoElementSubPosConfig,
    },
    opdekdeur: {
      texture: interiorClosed1Unit2UnitTexture,
      subtractionPositionConfig: defaultOneTwoElementSubPosConfig,
    },
    stompedeur: {
      texture: interiorClosed1Unit2UnitTexture,
      subtractionPositionConfig: defaultOneTwoElementSubPosConfig,
    },
    schuifdeur: {
      // Old configuration
      // texture: interiorClosed1Unit2UnitTexture,
      // subtractionPositionConfig: {
      //   ...defaultOneTwoElementSubPosConfig,
      //   xPosition: -0.44,
      //   // yPosition: 0,
      // },
      texture: interiorClosed3Unit4UnitTexture,
      subtractionPositionConfig: {
        ...defaultThreeElementSubPosConfig,
        xPosition: -0.345,
        yPosition: 0.29,
      },
      biggerSceneNeeded: true,
    },
    taatsdeur: {
      texture: interiorClosed1Unit2UnitTexture,
      subtractionPositionConfig: defaultOneTwoElementSubPosConfig,
      leftMargin: 0.015,
      rightMargin: 0.015,
      topMargin: 0,
      bottomMargin: 0.012,
    },
    paneel: {
      texture: interiorClosed1Unit2UnitTexture,
      subtractionPositionConfig: defaultOneTwoElementSubPosConfig,
      bottomMargin: 0.01,
    },
    scharnierdeur: {
      texture: interiorClosed1Unit2UnitTexture,
      subtractionPositionConfig: defaultOneTwoElementSubPosConfig,
      bottomMargin: -0.02,
      topMargin: -0.03,
    },
    paneelpaneel: {
      texture: interiorClosed1Unit2UnitTexture,
      subtractionPositionConfig: defaultOneTwoElementSubPosConfig,
    },
    paneelpaneelpaneel: {
      texture: interiorClosed3Unit4UnitTexture,
      subtractionPositionConfig: defaultThreeElementSubPosConfig,
    },
    paneelpaneelpaneelpaneel: {
      texture: interiorClosed3Unit4UnitTexture,
      subtractionPositionConfig: paneel4x,
    },
    dubbel: {
      texture: interiorClosed1Unit2UnitTexture,
      subtractionPositionConfig: defaultOneTwoElementSubPosConfig,
      // leftMargin: 0.006,
      // rightMargin: 0.01,
      // topMargin: 0.012,
      // bottomMargin: 0.012,
    },
    dubbel1paneel: {
      texture: interiorClosed3Unit4UnitTexture,
      subtractionPositionConfig: {
        ...defaultThreeElementSubPosConfig,
        yPosition: eigenwijs?.sizeObjects[0].customHeight
          ? eigenwijs?.sizeObjects[0].customHeight >= 1900 &&
            eigenwijs?.sizeObjects[0].customHeight <= 2000
            ? 0.345
            : eigenwijs?.sizeObjects[0].customHeight >= 2600 &&
                eigenwijs?.sizeObjects[0].customHeight <= 2799
              ? 0.355
              : eigenwijs?.sizeObjects[0].customHeight >= 2800
                ? 0.342
                : 0.347 +
                  0.003 *
                    ((eigenwijs?.sizeObjects[0].customHeight - 2000) / 100)
          : 0,
      },

      topMargin: -0.021,
      bottomMargin: 0,
    },
    dubbel2paneel: {
      texture: interiorClosed3Unit4UnitTexture,
      subtractionPositionConfig: paneel4x,
      topMargin: -0.011,
      bottomMargin: -0.011,
    },
    taatsdeurtaatsdeurpaneelpaneel: {
      texture: interiorClosed3Unit4UnitTexture,
      subtractionPositionConfig: paneel4x,
      topMargin: -0.011,
      bottomMargin: -0.011,
    },
    taatsdeurpaneelpaneel: {
      texture: interiorClosed3Unit4UnitTexture,
      subtractionPositionConfig: defaultThreeElementSubPosConfig,
      topMargin: 0.001,
      bottomMargin: 0.011,
    },
    taatsdeur2paneel: {
      texture: interiorClosed3Unit4UnitTexture,
      subtractionPositionConfig: defaultThreeElementSubPosConfig,
      topMargin: 0.001,
      bottomMargin: 0.008,
    },
    taatsdeur1paneel: {
      texture: interiorClosed1Unit2UnitTexture,
      subtractionPositionConfig: defaultOneTwoElementSubPosConfig,
      // leftMargin: 0.015,
      topMargin: -0.005,
      bottomMargin: 0.012,
    },
    schuifdeurpaneelpaneel: {
      texture: interiorClosed3Unit4UnitTexture,
      subtractionPositionConfig: defaultThreeElementSubPosConfig,
    },
    schuifdeurpaneelpaneelschuifdeur: {
      texture: interiorClosed3Unit4UnitTexture,
      subtractionPositionConfig: paneel4x,
      rightMargin: 0.028,
      bottomMargin: -0.02,
      topMargin: -0.004,
    },
    schuifdeurschuifdeurpaneelpaneel: {
      texture: interiorClosed3Unit4UnitTexture,
      subtractionPositionConfig: paneel4x,
      rightMargin: 0.028,
      bottomMargin: -0.02,
      topMargin: -0.004,
    },
    schuifdeurpaneelschuifdeur: {
      texture: interiorClosed3Unit4UnitTexture,
      subtractionPositionConfig: defaultThreeElementSubPosConfig,
    },
    schuifpaneelschuiftrap: {
      texture: interiorClosedStairsTexture,
      subtractionPositionConfig: defaultStairSubPosConfig,
    },
    dubbeleschuifdeur1paneel: {
      texture: interiorClosed3Unit4UnitTexture,
      subtractionPositionConfig: {
        ...defaultThreeElementSubPosConfig,
        xPosition: 0.3,
      },
    },
    dubbeleschuifdeur2paneel: {
      texture: interiorClosed3Unit4UnitTexture,
      subtractionPositionConfig: paneel4x,
      rightMargin: 0.028,
    },
    dubbeleschuifdeur: {
      texture: interiorClosed3Unit4UnitTexture,
      subtractionPositionConfig: {
        ...defaultThreeElementSubPosConfig,
        xPosition: -0.01,
      },
      biggerSceneNeeded: true,
    },
    schuifdeur2paneel: {
      texture: interiorClosed3Unit4UnitTexture,
      subtractionPositionConfig: defaultThreeElementSubPosConfig,
    },
    schuifdeur1paneel: {
      texture: interiorClosed1Unit2UnitTexture,
      subtractionPositionConfig: defaultOneTwoElementSubPosConfig,
    },
    scharnierdeur1paneel: {
      texture: interiorClosed1Unit2UnitTexture,
      subtractionPositionConfig: defaultOneTwoElementSubPosConfig,
      bottomMargin: -0.02,
      topMargin: -0.03,
    },
    scharnierdeur2paneel: {
      texture: interiorClosed3Unit4UnitTexture,
      subtractionPositionConfig: defaultThreeElementSubPosConfig,
    },
    dubbelescharnierdeur: {
      texture: interiorClosed1Unit2UnitTexture,
      subtractionPositionConfig: defaultOneTwoElementSubPosConfig,
      bottomMargin: -0.02,
      topMargin: -0.03,
    },
    dubbelescharnierdeur1paneel: {
      texture: interiorClosed3Unit4UnitTexture,
      subtractionPositionConfig: defaultThreeElementSubPosConfig,
    },
    dubbelescharnierdeur2paneel: {
      texture: interiorClosed3Unit4UnitTexture,
      subtractionPositionConfig: paneel4x,
      bottomMargin: -0.02,
    },
    scharnierdeurpaneelscharnierdeur: {
      texture: interiorClosed3Unit4UnitTexture,
      subtractionPositionConfig: defaultThreeElementSubPosConfig,
    },
    scharnierpaneelscharniertrap: {
      texture: interiorClosedStairsTexture,
      subtractionPositionConfig: defaultStairSubPosConfig,
    },
    scharnierdeurpaneelpaneel: {
      texture: interiorClosed3Unit4UnitTexture,
      subtractionPositionConfig: defaultThreeElementSubPosConfig,
    },
    scharnierdeurpaneelpaneelscharnierdeur: {
      texture: interiorClosed3Unit4UnitTexture,
      subtractionPositionConfig: paneel4x,
      bottomMargin: -0.02,
    },
    scharnierdeurscharnierdeurpaneelpaneel: {
      texture: interiorClosed3Unit4UnitTexture,
      subtractionPositionConfig: paneel4x,
      bottomMargin: -0.02,
      topMargin: -0.01,
    },
  };

  const pos = new Vector3(0, 0.22, -0.2);

  const width = getConfigurationWidth(0);
  const { leftMarginMapping, rightMarginMapping, topMarginMapping } =
    getMarginMapping(door?.code ?? "", width);
  // Custom record for XLXXL doors
  // This is used to apply a different subtraction position and scale for XLXXL doors
  const XLXXLRecord = {
    texture: interiorClosed3Unit4UnitTexture,
    subtractionPositionConfig: paneel4x,
    leftMargin: leftMarginMapping,
    rightMargin: rightMarginMapping,
    topMargin: topMarginMapping,
  };

  useEffect(() => {
    if (eigenwijs) {
      if (door && door.configuration.nElements) {
        const record =
          newTextureXLXLDoors.includes(door.code) && isXLXXLScene
            ? // If the door is XLXXL and the scene is XLXXL, use the custom record
              XLXXLRecord
            : // otherwise use the foreground record
              foregroundRecord[door.code];

        updateSubtractionScale(record, door.configuration.nElements);
        updateSubtractionPosition(record, door.configuration.nElements);
      }
    }
    //  [eigenwijs?.sizeObjects, doorPositionXOffset]);
  }, [eigenwijs?.sizeObjects, doorPositionXOffset, width, XLXXLRecord]);

  const updateSubtractionScale = (
    config: ForegroundConfiguration,
    elements: number,
  ) => {
    if (eigenwijs && door) {
      let heightScale = eigenwijs.sizeObjects[0].customHeight / 1000;
      let widthScale = 0;

      heightScale += config.topMargin ?? 0;
      heightScale += config.bottomMargin ?? 0;

      eigenwijs.sizeObjects.forEach((sizeObject, index) => {
        widthScale += sizeObject.customWidth / 1000;

        if (sizeObject.customWidth / defaultDoorWidth < 1) {
          let offset =
            (Math.abs(sizeObject.customWidth - defaultDoorWidth) /
              (defaultDoorWidth - minDoorWidth)) *
            0.017;
          widthScale +=
            (Math.abs(sizeObject.customWidth - defaultDoorWidth) /
              (defaultDoorWidth - minDoorWidth)) *
            offset;
        } else if (sizeObject.customWidth / defaultDoorWidth > 1) {
          let offset =
            (Math.abs(sizeObject.customWidth - defaultDoorWidth) /
              (maxDoorWidth - defaultDoorWidth)) *
            0.013;
          widthScale -=
            (Math.abs(sizeObject.customWidth - defaultDoorWidth) /
              (maxDoorWidth - defaultDoorWidth)) *
            offset;
        } else {
          widthScale -= 0.0015;
        }

        if (eigenwijs.sizeObjects.length > 1) {
          widthScale +=
            index === 0 && config.leftMargin ? config.leftMargin : 0;
          widthScale +=
            index === eigenwijs.sizeObjects.length - 1 && config.rightMargin
              ? config.rightMargin
              : 0;
        } else {
          widthScale += config.leftMargin ?? 0;
          widthScale += config.rightMargin ?? 0;
        }
      });
      const newScale = new Vector3(
        widthScale * doorScale.x,
        heightScale * doorScale.y,
        2.5,
      );

      if (door?.code === "paneelpaneelpaneel") {
        newScale.x *= 1.02;
      }
      //    setSubtractionScale(
      // new Vector3(widthScale * doorScale.x, heightScale * doorScale.y, 2.5),
      if (!subtractionScale.equals(newScale)) {
        setSubtractionScale(newScale);
      }
    }
  };

  const updateSubtractionPosition = (
    config: ForegroundConfiguration,
    elements: number,
  ) => {
    if (eigenwijs) {
      let yPosition = config.subtractionPositionConfig.yPosition;
      let xPosition = config.subtractionPositionConfig.xPosition;

      let yOffset = 0;
      let xOffset = 0;

      if (eigenwijs.sizeObjects[0].customHeight < defaultDoorHeight) {
        yOffset =
          (Math.abs(eigenwijs.sizeObjects[0].customHeight - defaultDoorHeight) /
            660) *
          config.subtractionPositionConfig.minYPosition;
      } else if (eigenwijs.sizeObjects[0].customHeight > defaultDoorHeight) {
        yOffset =
          (Math.abs(eigenwijs.sizeObjects[0].customHeight - defaultDoorHeight) /
            440) *
          config.subtractionPositionConfig.maxYPosition;
      }

      eigenwijs.sizeObjects.forEach((sizeObject, index) => {
        if (elements > 1) {
          let p = 0;
          if (sizeObject.customWidth < defaultDoorWidth) {
            p =
              (Math.abs(sizeObject.customWidth - defaultDoorWidth) / 320) *
              config.subtractionPositionConfig.minXPosition;
          } else if (sizeObject.customWidth > defaultDoorWidth) {
            p =
              (Math.abs(sizeObject.customWidth - defaultDoorWidth) / 180) *
              config.subtractionPositionConfig.maxXPosition;
          }

          if (index === 0) {
            xOffset =
              sizeObject.customWidth === defaultDoorWidth
                ? 0
                : sizeObject.customWidth < defaultDoorWidth
                  ? p * doorScale.x
                  : -p * doorScale.x;
          } else {
            xOffset +=
              sizeObject.customWidth === defaultDoorWidth
                ? 0
                : sizeObject.customWidth < defaultDoorWidth
                  ? -p * doorScale.x
                  : p * doorScale.x;
          }
        }
      });

      xPosition += xOffset + doorPositionXOffset;
      yPosition += yOffset;

      if (config.leftMargin) {
        xPosition -= config.leftMargin;
      }
      if (config.rightMargin) {
        xPosition += config.rightMargin;
      }
      if (config.topMargin) {
        yPosition += config.topMargin;
      }
      if (config.bottomMargin) {
        yPosition -= config.bottomMargin;
      }

      // setSubtractionPosition(new Vector3(xPosition, yPosition, 0));
      const newPosition = new Vector3(xPosition, yPosition, 0);
      if (!subtractionPosition.equals(newPosition)) {
        setSubtractionPosition(newPosition);
      }
    }
  };

  const getForegroundRecordTexture = (code: string): Texture => {
    if (newTextureXLXLDoors.includes(code) && isXLXXLScene) {
      return interiorXLXXLTexture;
    }

    if (layout && layout.code === "eigenwijs" && code.includes("trap")) {
      return interiorStudioTexture;
    } else {
      return foregroundRecord[code].texture;
    }
  };

  const getScenePlaneSize = (): [number?, number?, number?, number?] => {
    const smallScene: [number?, number?, number?, number?] = [4.4, 4.2, 1];
    const bigScene: [number?, number?, number?, number?] = [5.475, 4.75, 1];
    const XLXXLScene: [number?, number?, number?, number?] = [5.475, 4.2, 1];

    if (door && newTextureXLXLDoors.includes(door.code) && isXLXXLScene)
      return XLXXLScene;
    if (eigenwijs) {
      if (door && door.configuration.nElements) {
        const record = foregroundRecord[door.code];
        const elements = door.configuration.nElements;
        if (elements > 2) {
          if (door && door.code.includes("trap")) {
            return smallScene;
          } else {
            return bigScene;
          }
        } else {
          return record.biggerSceneNeeded ? bigScene : smallScene;
        }
      }
    }

    return smallScene;
  };

  return (
    <>
      {door && (
        <mesh>
          <mesh>
            <Geometry>
              <Base name={"base"} position={[0, 0, pos.z + -0.01]}>
                <planeGeometry args={[10, 10, 1]} />
              </Base>
              <mesh>
                <Subtraction position={subtractionPosition}>
                  <boxGeometry
                    args={[
                      subtractionScale.x,
                      subtractionScale.y,
                      subtractionScale.z,
                    ]}
                  />
                </Subtraction>
              </mesh>
            </Geometry>
            <meshBasicMaterial transparent opacity={0} />
          </mesh>
          <mesh>
            <Geometry>
              <Base name={"base"} position={pos}>
                <planeGeometry args={getScenePlaneSize()} />
              </Base>
              <mesh>
                <Subtraction position={subtractionPosition}>
                  <boxGeometry
                    args={[
                      subtractionScale.x,
                      subtractionScale.y,
                      subtractionScale.z,
                    ]}
                  />
                </Subtraction>
              </mesh>
            </Geometry>
            <meshStandardMaterial map={getForegroundRecordTexture(door.code)} />
          </mesh>
        </mesh>
      )}
    </>
  );
};
export default Foreground;
